import { environment } from '../../../config/config';
import { uniq, uniqBy } from 'lodash';
import { Injectable } from '@angular/core';
import { CUSTOM_SHAPE_LIB_ID_PREFIX } from '../../library/abstract-shape-library';
import { TranslateService } from '@ngx-translate/core';

/**
 * Interface for a library list item
 * used by the {@link LibraryList} component.
 */
export interface ILibraryListItem {
    /**
     * Library ID.
     */
    id: string;

    /**
     * The name of the library thats visible to the user.
     */
    label: string;

    /**
     * Categories that the library belongs to. A library can belong
     * to multiple categories.
     */
    groups: string[];

    /**
     * FAB display group
     */
    libGroup: string;

    /**
     * Library browser categories
     */
    category: string;

    /**
     * This is to specify top shapes to switch to in this library
     * ie most used shapes in the library with switch preferences
     */
    switchDefaults?: {
        defaultPreferences: any,
        shapes: {
            id: string,
            defId: string,
            data: any,
            defaultPreferences: any,
        },
    };
}

export interface ILibraryCategoryDefinition {

    /**
     * Identifies the category
     */
    id: string;

    /**
     * Name of the category
     */
    label: string;

    /**
     * A short description of the library category
     */
    description: string;

    /**
     * Descriptions for groups within the category
     */
    groups?: {[ groupId: string ]: { groupId?: string, description?: string, link?: string }};

    /**
     * Link to article explaining more about the library category and the libraries/groups within
     */
    link?: string;

    /**
     * Path to graphical representation of the library category, svg
     */
    graphic?: string;
}

/**
 * The library list class is used to manage the library list and all
 * the libraries that are available for the user is stored in this class.
 * There are several methods available that allows you to retrieve the desired
 * data related to a single library or a group of libraries.
 */
@Injectable()
export class LibraryList {

    /**
     * Array containing all the available libraries in Nucleus
     * which the users can add from the add library menu.
     */
    protected allShapeLibraries: Array<ILibraryListItem> = [
        { id: 'common-shapes', label: 'LABELS.LIBRARY_NAMES.SHAPES', groups: [], libGroup: 'Shapes', category: 'Basics' },
        { id: 'reaction', label: 'LABELS.LIBRARY_NAMES.REACTIONS', groups: [], libGroup: 'Shapes', category: 'Basics' },
        { id: 'projects', label: 'LABELS.LIBRARY_NAMES.PROJECT_MANAGEMENT', groups: [ 'Projects ' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'people', label: 'LABELS.LIBRARY_NAMES.HUMAN_RESOURCE_MANAGEMENT', groups: [ 'Human Resources' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'arrows', label: 'LABELS.LIBRARY_NAMES.ARROWS', groups: [], libGroup: 'Shapes', category: 'Basics' },
        { id: 'archimate-application', label: 'LABELS.LIBRARY_NAMES.ARCHIMATE_APPLICATION', groups: [ 'Archimate' ],
            libGroup: 'Archimate', category: 'Technical' },
        { id: 'archimate-business', label: 'LABELS.LIBRARY_NAMES.ARCHIMATE_BUSINESS', groups: [ 'Archimate' ],
            libGroup: 'Archimate', category: 'Technical' },
        { id: 'archimate-composite', label: 'LABELS.LIBRARY_NAMES.ARCHIMATE_COMPOSITE', groups: [ 'Archimate' ],
            libGroup: 'Archimate', category: 'Technical' },
        { id: 'archimate-implementation', label: 'LABELS.LIBRARY_NAMES.ARCHIMATE_IMPLEMENTATION', groups: [ 'Archimate' ],
            libGroup: 'Archimate', category: 'Technical' },
        { id: 'archimate-motivation', label: 'LABELS.LIBRARY_NAMES.ARCHIMATE_MOTIVATION', groups: [ 'Archimate' ],
            libGroup: 'Archimate', category: 'Technical' },
        { id: 'archimate-physical', label: 'LABELS.LIBRARY_NAMES.ARCHIMATE_PHYSICAL', groups: [ 'Archimate' ],
            libGroup: 'Archimate', category: 'Technical' },
        { id: 'archimate-strategy', label: 'LABELS.LIBRARY_NAMES.ARCHIMATE_STRATEGY', groups: [ 'Archimate' ],
            libGroup: 'Archimate', category: 'Technical' },
        { id: 'archimate-technology', label: 'LABELS.LIBRARY_NAMES.ARCHIMATE_TECHNOLOGY', groups: [ 'Archimate' ],
            libGroup: 'Archimate', category: 'Technical' },
        { id: 'creately-aws-a', label: 'LABELS.LIBRARY_NAMES.AWS_ANALYTICS', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-app-integration', label: 'LABELS.LIBRARY_NAMES.AWS_APP_INTEGRATION', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-ar-vr', label: 'LABELS.LIBRARY_NAMES.AWS_AR_VR', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-blockchain', label: 'LABELS.LIBRARY_NAMES.AWS_BLOCKCHAIN', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-business-applications', label: 'LABELS.LIBRARY_NAMES.AWS_BUSINESS_APPLICATIONS', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-cdn', label: 'LABELS.LIBRARY_NAMES.AWS_CDN', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-compute', label: 'LABELS.LIBRARY_NAMES.AWS_COMPUTE', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-cost-management', label: 'LABELS.LIBRARY_NAMES.AWS_COST_MANAGEMENT', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-customer-engagement', label: 'LABELS.LIBRARY_NAMES.AWS_CUSTOMER_ENGAGEMENT', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-database', label: 'LABELS.LIBRARY_NAMES.AWS_DATABASE', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-general', label: 'LABELS.LIBRARY_NAMES.AWS_GENERAL', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-governance', label: 'LABELS.LIBRARY_NAMES.AWS_GOVERNANCE', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-group-icons', label: 'LABELS.LIBRARY_NAMES.AWS_GROUP_ICONS', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-iot', label: 'LABELS.LIBRARY_NAMES.AWS_IOT', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-media', label: 'LABELS.LIBRARY_NAMES.AWS_MEDIA', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-ml', label: 'LABELS.LIBRARY_NAMES.AWS_ML', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-mobile', label: 'LABELS.LIBRARY_NAMES.AWS_MOBILE', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-other', label: 'LABELS.LIBRARY_NAMES.AWS_OTHER', groups: [ 'Amazon Web Services (AWS)' ],
        libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-security', label: 'LABELS.LIBRARY_NAMES.AWS_SECURITY', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'aws-storage', label: 'LABELS.LIBRARY_NAMES.AWS_STORAGE', groups: [ 'Amazon Web Services (AWS)' ],
            libGroup: 'AWS', category: 'Technical' },
        { id: 'grids-boards', label: 'LABELS.LIBRARY_NAMES.GRIDS_AND_BOARDS', groups: [  ], libGroup: 'Shapes', category: 'Frames' },
        { id: 'grids-canvases', label: 'LABELS.LIBRARY_NAMES.GRIDS_AND_CANVASES', groups: [  ],
            libGroup: 'Shapes', category: 'Frames' },
        { id: 'slides', label: 'LABELS.LIBRARY_NAMES.SLIDES', groups: [ ], libGroup: 'Frames', category: 'Frames' },
        { id: 'frames', label: 'LABELS.LIBRARY_NAMES.PAPER_DEVICE_FRAMES', groups: [  ], libGroup: 'Shapes', category: 'Frames' },
        { id: 'swimlanes', label: 'LABELS.LIBRARY_NAMES.SWIMLANES', groups: [  ], libGroup: 'Shapes', category: 'Frames' },
        { id: 'block', label: 'LABELS.LIBRARY_NAMES.BLOCK_SHAPES', groups: [], libGroup: 'Shapes', category: 'Basics' },
        { id: 'bpmn', label: 'LABELS.LIBRARY_NAMES.BPMN_SHAPES', groups: [ 'Process' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'chemistry-lab-equipment', label: 'LABELS.LIBRARY_NAMES.CHEMISTRY_LAB', groups: [],
            libGroup: 'Others', category: 'Others' },
        { id: 'concept-map', label: 'LABELS.LIBRARY_NAMES.CONCEPT_MAPS', groups: [ 'General' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'dfd-gs', label: 'LABELS.LIBRARY_NAMES.DATA_FLOW_DIAGRAMS_GS', groups: [ 'Database, ER & DFD' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'dfd-yc', label: 'LABELS.LIBRARY_NAMES.DATA_FLOW_DIAGRAMS_YC', groups: [ 'Database, ER & DFD' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'entity-relationship-cf', label: 'LABELS.LIBRARY_NAMES.ENTITY_RELATIONSHIP_CROWS_FOOT', groups: [ 'Database, ER & DFD' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'entity-relationship-chens', label: 'LABELS.LIBRARY_NAMES.ENTITY_RELATIONSHIP_CHENS', groups: [ 'Database, ER & DFD' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'entity-relationship-bachman', label: 'LABELS.LIBRARY_NAMES.ENTITY_RELATIONSHIP_BACHMAN', groups: [ 'Database, ER & DFD' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'entity-relationship-barkers', label: 'LABELS.LIBRARY_NAMES.ENTITY_RELATIONSHIP_BARKER', groups: [ 'Database, ER & DFD' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'flowchart', label: 'LABELS.LIBRARY_NAMES.FLOWCHART', groups: [ 'Process', 'General' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'gantt', label: 'LABELS.LIBRARY_NAMES.GANTT_CHART', groups: [ 'Projects ' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'general', label: 'LABELS.LIBRARY_NAMES.BASIC_SHAPES', groups: [], libGroup: 'Shapes', category: 'Basics' },
        { id: 'genogram', label: 'LABELS.LIBRARY_NAMES.BASIC_GENOGRAM_SHAPES', groups: [ 'Basic Genogram' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'genogram-medical', label: 'LABELS.LIBRARY_NAMES.GENOGRAM_MEDICAL_SHAPES', groups: [ 'Extended Genogram' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'genogram-colored', label: 'LABELS.LIBRARY_NAMES.GENOGRAM_COLORED_SHAPES', groups: [ 'Extended Genogram' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'genogram-gender-based', label: 'LABELS.LIBRARY_NAMES.GENOGRAM_GENDER_BASED_SHAPES', groups: [ 'Extended Genogram' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'venn', label: 'LABELS.LIBRARY_NAMES.VENN_DIAGRAM', groups: [ 'General' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'popular-diagrams', label: 'LABELS.LIBRARY_NAMES.POPULAR_DIAGRAMS', groups: [ 'General' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'sticky', label: 'LABELS.LIBRARY_NAMES.STICKY_PACKS', groups: [ 'General' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'timeline', label: 'LABELS.LIBRARY_NAMES.TIMELINE_DIAGRAM', groups: [ 'Projects ' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'infographics', label: 'LABELS.LIBRARY_NAMES.INFOGRAPHICS', groups: [ 'Communications' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'fault-tree-analysis', label: 'LABELS.LIBRARY_NAMES.FAULT_TREE_ANALYSIS', groups: [ 'Process' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'vsm', label: 'LABELS.LIBRARY_NAMES.VALUE_STREAM_MAP', groups: [ 'Process' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'mindmap', label: 'LABELS.LIBRARY_NAMES.MINDMAPS', groups: [ 'Projects ' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'network-rack', label: 'LABELS.LIBRARY_NAMES.NETWORK_RACK_SHAPES', groups: [ 'Network Diagrams' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'org-chart', label: 'LABELS.LIBRARY_NAMES.ORGANIZATIONAL_CHARTS', groups: [ 'Human Resources' ],
            libGroup: 'Shapes', category: 'Business' },
        { id: 'simple-network', label: 'LABELS.LIBRARY_NAMES.NETWORK_SHAPES', groups: [ 'Network Diagrams' ], libGroup: 'Shapes', category: 'Technical' },
        { id: 'site-map', label: 'LABELS.LIBRARY_NAMES.SITE_MAPS_UX_FLOWS', groups: [ 'Product' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'stars-and-balloons', label: 'LABELS.LIBRARY_NAMES.STARS_AND_BALLOONS', groups: [  ], libGroup: 'Shapes', category: 'Basics' },
        { id: 'swot', label: 'LABELS.LIBRARY_NAMES.SWOT_DIAGRAMS', groups: [ 'Projects ' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'bcg-matrix', label: 'LABELS.LIBRARY_NAMES.BCG_MATRIX', groups: [ 'Projects ' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'tqm', label: 'LABELS.LIBRARY_NAMES.TOTAL_QUALITY_MANAGEMENT_TQM', groups: [ 'Process' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'ui-mockup', label: 'LABELS.LIBRARY_NAMES.UI_MOCKUP', groups: [ 'Product' ], libGroup: 'Shapes', category: 'Business' },
        { id: 'uml-activity', label: 'LABELS.LIBRARY_NAMES.UML_ACTIVITY', groups: [ 'UML Modelling' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'uml-class', label: 'LABELS.LIBRARY_NAMES.UML_CLASS', groups: [ 'UML Modelling' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'uml-communication', label: 'LABELS.LIBRARY_NAMES.UML_COLLABORATION', groups: [ 'UML Modelling' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'uml-sequence', label: 'LABELS.LIBRARY_NAMES.UML_SEQUENCE', groups: [ 'UML Modelling' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'uml-database', label: 'LABELS.LIBRARY_NAMES.DATABASE_DIAGRAM', groups: [ 'UML Modeling' ], libGroup: 'Shapes', category: 'Technical' },
        { id: 'database', label: 'LABELS.LIBRARY_NAMES.DATABASE_DIAGRAM', groups: [ 'Database, ER & DFD' ], libGroup: 'Shapes', category: 'Technical' },
        { id: 'uml-state-machine', label: 'LABELS.LIBRARY_NAMES.UML_STATE_MACHINE', groups: [ 'UML Modelling' ],
            libGroup: 'Shapes', category: 'Technical' },
        // { id: 'uml-deployment', label: 'UML Deployment', groups: [ 'Software' ], libGroup: 'Shapes' },
        // { id: 'uml-component', label: 'UML Component', groups: [ 'Software' ], libGroup: 'Shapes' },
        // { id: 'uml-domain-model', label: 'UML Domain Model', groups: [ 'Software' ], libGroup: 'Shapes' },
        { id: 'use-case', label: 'LABELS.LIBRARY_NAMES.UML_USE_CASE', groups: [ 'UML Modelling' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'cisco-products', label: 'LABELS.LIBRARY_NAMES.CISCO_PRODUCTS', groups: [ 'Network Diagrams' ], libGroup: 'Shapes', category: 'Technical' },
        { id: 'cisco-miscellaneous', label: 'LABELS.LIBRARY_NAMES.CISCO_MISCELLANEOUS', groups: [ 'Network Diagrams' ],
            libGroup: 'Shapes', category: 'Technical' },
        { id: 'gcp-ai-and-machine-learning', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_AI_MACHINE_LEARNING', groups: [ 'Google Cloud Platform (GCP)' ],
            libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-api-management', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_API_MANAGEMENT', groups: [ 'Google Cloud Platform (GCP)' ],
            libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-compute', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_COMPUTE', groups: [ 'Google Cloud Platform (GCP)' ],
            libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-data-analytics', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_DATA_ANALYTICS', groups: [ 'Google Cloud Platform (GCP)' ],
            libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-databases', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_DATABASES', groups: [ 'Google Cloud Platform (GCP)' ],
            libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-developer-tools', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_DEVELOPER_TOOLS', groups: [ 'Google Cloud Platform (GCP)' ],
            libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-hybrid-and-multi-cloud', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_HYBRID_MULTI_CLOUD', groups: [ 'Google Cloud Platform (GCP)' ], libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-internet-of-things', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_IOT', groups: [ 'Google Cloud Platform (GCP)' ], libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-management-tools', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_MANAGEMENT_TOOLS', groups: [ 'Google Cloud Platform (GCP)' ],
            libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-migration', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_MIGRATION', groups: [ 'Google Cloud Platform (GCP)' ], libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-networking', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_NETWORKING', groups: [ 'Google Cloud Platform (GCP)' ], libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-security', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_SECURITY', groups: [ 'Google Cloud Platform (GCP)' ],
            libGroup: 'GCP', category: 'Technical' },
        { id: 'gcp-storage', label: 'LABELS.LIBRARY_NAMES.GOOGLE_CLOUD_STORAGE', groups: [ 'Google Cloud Platform (GCP)' ],
            libGroup: 'GCP', category: 'Technical' },
        { id: 'azure-ai-and-ml', label: 'LABELS.LIBRARY_NAMES.AZURE_AI_AND_ML', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-deprecated', label: 'LABELS.LIBRARY_NAMES.AZURE_DEPRECATED', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-iot', label: 'LABELS.LIBRARY_NAMES.AZURE_IOT', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-other', label: 'LABELS.LIBRARY_NAMES.AZURE_OTHER', groups: [ 'Microsoft Azure' ], libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-analytics', label: 'LABELS.LIBRARY_NAMES.AZURE_ANALYTICS', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-dev-ops', label: 'LABELS.LIBRARY_NAMES.AZURE_DEVOPS', groups: [ 'Microsoft Azure' ], libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-management-governance', label: 'LABELS.LIBRARY_NAMES.AZURE_MANAGEMENT_GOVERNANCE', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-security', label: 'LABELS.LIBRARY_NAMES.AZURE_SECURITY', groups: [ 'Microsoft Azure' ], libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-companies', label: 'LABELS.LIBRARY_NAMES.AZURE_COMPANIES', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-general', label: 'LABELS.LIBRARY_NAMES.AZURE_GENERAL', groups: [ 'Microsoft Azure' ], libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-migrate', label: 'LABELS.LIBRARY_NAMES.AZURE_MIGRATE', groups: [ 'Microsoft Azure' ], libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-storage', label: 'LABELS.LIBRARY_NAMES.AZURE_STORAGE', groups: [ 'Microsoft Azure' ], libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-compute', label: 'LABELS.LIBRARY_NAMES.AZURE_COMPUTE', groups: [ 'Microsoft Azure' ], libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-identity', label: 'LABELS.LIBRARY_NAMES.AZURE_IDENTITY', groups: [ 'Microsoft Azure' ], libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-mixed-reality', label: 'LABELS.LIBRARY_NAMES.AZURE_MIXED_REALITY', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-web', label: 'LABELS.LIBRARY_NAMES.AZURE_WEB', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-container', label: 'LABELS.LIBRARY_NAMES.AZURE_CONTAINER', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-integration', label: 'LABELS.LIBRARY_NAMES.AZURE_INTEGRATION', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-mobile', label: 'LABELS.LIBRARY_NAMES.AZURE_MOBILE', groups: [ 'Microsoft Azure' ], libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-databases', label: 'LABELS.LIBRARY_NAMES.AZURE_DATABASES', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-intune', label: 'LABELS.LIBRARY_NAMES.AZURE_INTUNE', groups: [ 'Microsoft Azure' ], libGroup: 'Azure', category: 'Technical' },
        { id: 'azure-networking', label: 'LABELS.LIBRARY_NAMES.AZURE_NETWORKING', groups: [ 'Microsoft Azure' ],
            libGroup: 'Azure', category: 'Technical' },
        { id: 'kubernetes-cluster-config', label: 'LABELS.LIBRARY_NAMES.KUBERNETES_CLUSTER_CONFIG', groups: [ 'Kubernetes (K8s)' ], libGroup: 'K8s', category: 'Technical' },
        { id: 'kubernetes-compute', label: 'LABELS.LIBRARY_NAMES.KUBERNETES_COMPUTE', groups: [ 'Kubernetes (K8s)' ],
            libGroup: 'K8s', category: 'Technical' },
        { id: 'kubernetes-control-plane-comp', label: 'LABELS.LIBRARY_NAMES.KUBERNETES_CONTROL_PLANE_COMPONENTS', groups: [ 'Kubernetes (K8s)' ], libGroup: 'K8s', category: 'Technical' },
        { id: 'kubernetes-group-link', label: 'LABELS.LIBRARY_NAMES.KUBERNETES_GROUPS_LINKS', groups: [ 'Kubernetes (K8s)' ], libGroup: 'K8s', category: 'Technical' },
        { id: 'kubernetes-infrastructure', label: 'LABELS.LIBRARY_NAMES.KUBERNETES_INFRASTRUCTURE', groups: [ 'Kubernetes (K8s)' ], libGroup: 'K8s', category: 'Technical' },
        { id: 'kubernetes-network', label: 'LABELS.LIBRARY_NAMES.KUBERNETES_NETWORK', groups: [ 'Kubernetes (K8s)' ],
            libGroup: 'K8s', category: 'Technical' },
        { id: 'kubernetes-others', label: 'LABELS.LIBRARY_NAMES.KUBERNETES_OTHERS', groups: [ 'Kubernetes (K8s)' ],
            libGroup: 'K8s', category: 'Technical' },
        { id: 'kubernetes-pods-config', label: 'LABELS.LIBRARY_NAMES.KUBERNETES_PODS_CONFIGURATION', groups: [ 'Kubernetes (K8s)' ],
            libGroup: 'K8s', category: 'Technical' },
        { id: 'kubernetes-rbac-model', label: 'LABELS.LIBRARY_NAMES.KUBERNETES_RBAC_MODEL', groups: [ 'Kubernetes (K8s)' ],
            libGroup: 'K8s', category: 'Technical' },
        { id: 'kubernetes-storage', label: 'LABELS.LIBRARY_NAMES.KUBERNETES_STORAGE', groups: [ 'Kubernetes (K8s)' ],
            libGroup: 'K8s', category: 'Technical' },
        { id: 'pf-chemical-and-process-engineering', label: 'LABELS.LIBRARY_NAMES.CHEMICAL_PROCESS_ENGINEERING', groups: [ 'Process Flow' ],
            libGroup: 'Process Flow', category: 'Others' },
        { id: 'pf-heating-equipment', label: 'LABELS.LIBRARY_NAMES.HEATING_EQUIPMENT', groups: [ 'Process Flow' ],
            libGroup: 'Process Flow', category: 'Others' },
        { id: 'pf-industrial-equipment', label: 'LABELS.LIBRARY_NAMES.INDUSTRIAL_EQUIPMENT', groups: [ 'Process Flow' ],
            libGroup: 'Process Flow', category: 'Others' },
        { id: 'pf-instruments', label: 'LABELS.LIBRARY_NAMES.INSTRUMENTS', groups: [ 'Process Flow' ],
            libGroup: 'Process Flow', category: 'Others' },
        { id: 'pf-pumps', label: 'LABELS.LIBRARY_NAMES.PUMPS', groups: [ 'Process Flow' ],
            libGroup: 'Process Flow', category: 'Others' },
        { id: 'pf-valves-and-fittings', label: 'LABELS.LIBRARY_NAMES.VALVES_AND_FITTINGS', groups: [ 'Process Flow' ],
            libGroup: 'Process Flow', category: 'Others' },
        { id: 'pf-vessels', label: 'LABELS.LIBRARY_NAMES.VESSELS', groups: [ 'Process Flow' ],
            libGroup: 'Process Flow', category: 'Others' },
        { id: 'pf-process-annotation', label: 'LABELS.LIBRARY_NAMES.PROCESS_ANNOTATION', groups: [ 'Process Flow' ],
            libGroup: 'Process Flow', category: 'Others' },
        { id: 'logic-gate-basic', label: 'LABELS.LIBRARY_NAMES.BASICS', groups: [ 'Logic Gates' ],
            libGroup: 'Logic Gates', category: 'Technical' },
        { id: 'logic-gate-ice', label: 'LABELS.LIBRARY_NAMES.ICE', groups: [ 'Logic Gates' ],
            libGroup: 'Logic Gates', category: 'Technical' },
        { id: 'logic-gate-flip-flops', label: 'LABELS.LIBRARY_NAMES.FLIP_FLOPS', groups: [ 'Logic Gates' ],
            libGroup: 'Logic Gates', category: 'Technical' },
        { id: 'logic-rs-latch', label: 'LABELS.LIBRARY_NAMES.RS_LATCH', groups: [ 'Logic Gates' ],
            libGroup: 'Logic Gates', category: 'Technical' },
        ...(
            ( environment.NAME !== 'stage' && environment.NAME !== 'dev' ) ? /* istanbul ignore next */[] : [
                {
                    id: 'experiments',
                    label: 'LABELS.LIBRARY_NAMES.EXPERIMENTS',
                    groups: [ 'Experiments' ],
                    libGroup: 'Shapes',
                    category: 'Others',
                },
            ]
        ),
    ];

    protected get allLibraryCategories(): Array<ILibraryCategoryDefinition>  {
        return [
            {
                id: 'Basics',
                label: 'LABELS.LIBRARY_CATEGORIES.BASICS',
                description: 'LABELS.LIBRARY_PANEL.BASICS_DESCRIPTION',
            },
            {
                id: 'Business',
                label: 'LABELS.LIBRARY_CATEGORIES.BUSINESS',
                description: 'LABELS.LIBRARY_PANEL.BUSINESS_DESCRIPTION',
                groups: {
                    'General': { groupId: 'General', description: 'LABELS.LIBRARY_PANEL.GENERAL_DESCRIPTION' },
                    'Projects ': { groupId: 'Projects', description: 'LABELS.LIBRARY_PANEL.PROJECTS_DESCRIPTION' },
                    'Human Resources': { groupId: 'Human Resources', description: 'LABELS.LIBRARY_PANEL.HUMAN_RESOURCES_DESCRIPTION' },
                    'Communications': { groupId: 'Communications', description: 'LABELS.LIBRARY_PANEL.COMMUNICATION_DESCRIPTION' },
                    'Process': { groupId: 'Process', description: 'LABELS.LIBRARY_PANEL.PROCESS_DESCRIPTION' },
                    'Product': { groupId: 'Product', description: 'LABELS.LIBRARY_PANEL.PRODUCT_DESCRIPTION' },
                    'Basic Genogram': {
                        groupId: 'Basic Genogram',
                        description: 'LABELS.LIBRARY_PANEL.GENOGRAMS_DESCRIPTION',
                        link: 'https://creately.com/guides/how-to-make-a-genogram/',
                    },
                    'Extended Genogram': { 
                        groupId: 'Extended Genogram', 
                        description: 'LABELS.LIBRARY_PANEL.GENOGRAMS_DESCRIPTION', 
                        link: 'https://creately.com/guides/how-to-make-a-genogram/'
                    },
                },
            },
            {
                id: 'Technical',
                label: 'LABELS.LIBRARY_CATEGORIES.TECHNICAL',
                description: 'LABELS.LIBRARY_PANEL.TECHNICAL_DESCRIPTION',
                groups: {
                    'Archimate': { groupId: 'Archimate', description: 'LABELS.LIBRARY_PANEL.ARCHIMATE_DESCRIPTION' },
                    'Amazon Web Services (AWS)': { groupId: 'Amazon Web Services (AWS)', description: 'LABELS.LIBRARY_PANEL.AWS_DESCRIPTION', link: 'https://creately.com/guides/aws-architecture-diagrams-and-use-cases/' },
                    'Database, ER & DFD': { groupId: 'Database, ER & DFD', description: 'LABELS.LIBRARY_PANEL.DATA_DESCRIPTION' },
                    'UML Modelling': { groupId: 'UML Modelling', description: 'LABELS.LIBRARY_PANEL.UML_DESCRIPTION', link: 'https://creately.com/blog/diagrams/uml-diagram-objects/' },
                    'Network Diagrams': { groupId: 'Network Diagrams', description: 'LABELS.LIBRARY_PANEL.NETWORKS_DESCRIPTION', link: 'https://creately.com/guides/network-diagram-guide-tutorial/' },
                    'Microsoft Azure': { groupId: 'Microsoft Azure', description: 'LABELS.LIBRARY_PANEL.MICROSOFT_AZURE_DESCRIPTION' },
                    'Kubernetes (K8s)': { groupId: 'Kubernetes (K8s)', description: 'LABELS.LIBRARY_PANEL.KUBERNETES_DESCRIPTION' },
                    'Logic Gates': { groupId: 'Logic Gates', description: 'LABELS.LIBRARY_PANEL.LOGIC_GATES_DESCRIPTION' },
                },
            },
            {
                id: 'Frames',
                label: 'LABELS.LIBRARY_CATEGORIES.FRAMES',
                description: 'LABELS.LIBRARY_PANEL.FRAME_DESCRIPTION',
            },
            {
                id: 'Others',
                label: 'LABELS.LIBRARY_CATEGORIES.OTHERS',
                description: 'LABELS.LIBRARY_PANEL.OTHERS_DESCRIPTION',
                groups: {
                    'Process Flow': {
                        groupId: 'Process Flow', 
                        description: 'LABELS.LIBRARY_PANEL.PROCESS_FLOW_DESCRIPTION', 
                        link: 'https://creately.com/guides/what-is-a-process-flow-diagram/'
    
                    }
                },
            },
            {
                id: 'Icons',
                label: 'LABELS.LIBRARY_CATEGORIES.ICONS',
                description: '',
            },
            {
                id: 'Custom Databases',
                label: 'LABELS.LIBRARY_CATEGORIES.CUSTOM_DATABASES',
                description: '',
            },
        ].map( v => {
            if ( v.description ) {
                v.description = this.translate.instant( v.description );
            }
            if ( v.label ) {
                v.label = this.translate.instant( v.label );
            }
            return v;
        })
    };

    protected allIconsLibraries: Array<ILibraryListItem> = [
        { id: 'icons-education', label: 'LABELS.LIBRARY_NAMES.ICONS_EDUCATION', groups: [ 'Icons' ],
            libGroup: 'Icons', category: 'Icons' },
        { id: 'icons-people-outlines', label: 'LABELS.LIBRARY_NAMES.ICONS_PEOPLE_OUTLINES', groups: [ 'Icons' ],
            libGroup: 'Icons', category: 'Icons' },
        { id: 'icons-people', label: 'LABELS.LIBRARY_NAMES.ICONS_PEOPLE', groups: [ 'Icons' ],
            libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-image-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_IMAGE', groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-file-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_FILE', groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-alert-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_ALERT', groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-action-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_ACTION', groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-notification-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_NOTIFICATION',
            groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-content-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_CONTENT', groups: [ 'Icons' ],
            libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-maps-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_MAPS', groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-social-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_SOCIAL', groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-navigation-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_NAVIGATION', groups: [ 'Icons' ],
            libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-hardware-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_HARDWARE', groups: [ 'Icons' ],
            libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-places-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_PLACES', groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-toggle-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_TOGGLE', groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-communication-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_COMMUNICATION',
            groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-av-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_AV', groups: [ 'Icons' ],
            libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-editor-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_EDITOR', groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
        { id: 'material-design-device-icons', label: 'LABELS.LIBRARY_NAMES.ICONS_MATERIAL_DESIGN_DEVICE', groups: [ 'Icons' ], libGroup: 'Icons', category: 'Icons' },
    ];

    protected quickToolLibraries: Array<ILibraryListItem> = [
        { id: 'quick-tool-stickers', label: 'LABELS.LIBRARY_NAMES.STICKERS', groups: [ 'Quick Tools' ], libGroup: 'Quick Tools', category: 'Quick Tools' },
        { id: 'quick-tool-database', label: 'LABELS.LIBRARY_NAMES.RECENT_DATABASE', groups: [ 'Quick Tools' ],
            libGroup: 'Quick Tools', category: 'Quick Tools' },
        { id: 'quick-tool-recent', label: 'LABELS.LIBRARY_NAMES.RECENT_SHAPES', groups: [ 'Quick Tools' ], libGroup: 'Quick Tools', category: 'Quick Tools' },
    ];

    protected embedsLibraries: Array<ILibraryListItem> = [
        { id: 'video', label: 'LABELS.LIBRARY_NAMES.VIDEO', groups: [ 'Embeds' ], libGroup: 'Embeds', category: 'Embeds' },
        { id: 'productivity', label: 'LABELS.LIBRARY_NAMES.PRODUCTIVITY', groups: [ 'Embeds' ], libGroup: 'Embeds', category: 'Embeds' },
        { id: 'devops', label: 'LABELS.LIBRARY_NAMES.DEVOPS', groups: [ 'Embeds' ], libGroup: 'Embeds', category: 'Embeds' },
    ];

    /**
     * Map of mismatching libIds in 'allShapeLibraries.id' fields
     * Add all mismatching libIds here to match with the required
     * corresponding id that is extracted from defId
     * Used to find whether id belongs to a premium group
     */
    protected mismatchingShapeLibraryIdsMap = [
        { libId: 'creately-aws-a', defId: 'aws-analytics' },
        { libId: 'aws-business-applications', defId: 'aws-business-apps' },
    ];

    /**
     * Premium library groups.
     */
    protected premiumGroups: string[] = [
        'Amazon Web Services (AWS)', 'Software', 'Google Cloud Platform (GCP)', 'Microsoft Azure', 'Kubernetes (K8s)', 'Network Diagrams', 'Extended Genogram',
    ];


    public constructor( private translate: TranslateService ) {}

    /**
     * Translates library item label from translation key to localized text
     */
    private translateLibraryItem(item: ILibraryListItem): ILibraryListItem {
        return {
            ...item,
            label: this.translate.instant(item.label)
        };
    }

    /**
     * Translates array of library items
     */
    private translateLibraryItems(items: ILibraryListItem[]): ILibraryListItem[] {
        return items.map(item => this.translateLibraryItem(item));
    }

    /**
     * Returns an array of premium library groups.
     */
    public getPremiumGroups(): string[] {
        return this.premiumGroups;
    }

    /**
     * Checks if a given library belongs to a premium group.
     * @param id the id for the library
     */
    public belongsToPremiumGroup( id: string ): boolean {
        let item = this.allShapeLibraries.find( libItem => libItem.id === id );

        /**
         * FIXME:
         * When extract libId from defId, Some library ids won't match with allShapeLibraries.id.
         * Instead, It only contains the prefix.
         *
         * HACK:
         * If couldn't find libraryId by id, Trying to find by prefix to solve above issue.
         */
        if ( !item ) {
            item = this.allShapeLibraries.find( libItem => libItem.id.startsWith( id ));
        }

        // If libraryId still cannot be found, check in 'mismatchingShapeLibraryIdsMap'
        if ( !item ) {
            const foundItem = Object.entries( this.mismatchingShapeLibraryIdsMap )
                .find(([ _, value ]) => value.defId === id );
            if ( foundItem ) {
                const originalItem = this.allShapeLibraries.find( libItem => libItem.id === foundItem[1].libId );
                if ( originalItem ) {
                    item = { ...originalItem, id: foundItem[1].defId };
                }
            }
        }

        return item ? item.groups.filter( group => this.premiumGroups.includes( group )).length !== 0 : false;
    }

    /**
     * Check whether the group is premium or not
     * @param group
     * @returns
     */
    public isPremiumGroup( group: string ): boolean {
        return this.premiumGroups.includes( group );
    }

    /**
     * Returns all the groups as an array.
     */
    public getAllGroups(): string[] {
        let groupArr = this.getAllShapeLibraries();
        groupArr = groupArr.concat( this.getAllIconsGroups());
        return uniq( groupArr );
    }

    /**
     * Returns all shape library IDs.
     */
    public getAllShapeLibraries(): string[] {
        const groupArr = [];
        this.allShapeLibraries.map( libItem => {
            libItem.groups.map( lib => {
                groupArr.push( lib );
            });
        });
        return uniq( groupArr );
    }

    /**
     * Returns all the various libraries
     */
    public getAllLibrarires(): ILibraryListItem[] {
        const libs = this.allShapeLibraries.concat(
            this.allIconsLibraries, this.embedsLibraries,
        );
        return this.translateLibraryItems(uniqBy( libs, 'id' ));
    }

    /**
     * Returns all the library category
     */
    public getAllLibraryCategories(): string[] {
        const categories  = [];
        this.allShapeLibraries.map( lib => categories.push( lib.category ));
        return uniq( categories );
    }

    /**
     * Returns all icon group IDs.
     */
    public getAllIconsGroups(): string[] {
        const groupArr = [];
        this.allIconsLibraries.map( libItem => {
            libItem.groups.map( lib => {
                groupArr.push( lib );
            });
        });
        return uniq( groupArr );
    }

    /**
     * Returns all icon group IDs.
     */
    public getAllEmbedsGroups(): string[] {
        const groupArr = [];
        this.embedsLibraries.map( libItem => {
            libItem.groups.map( lib => {
                groupArr.push( lib );
            });
        });
        return uniq( groupArr );
    }

    /**
     * Returns all icon libraries.
     */
    public getAllIconLibraries(): Array<ILibraryListItem> {
        return this.translateLibraryItems(this.allIconsLibraries);
    }


    /**
     * Returns all embeds libraries.
     */
    public getAllEmbedsLibraries(): Array<ILibraryListItem> {
        return this.translateLibraryItems(this.embedsLibraries);
    }

    /**
     * Returns all quick tool libraries.
     */
    public getQuickToolLibraries(): Array<ILibraryListItem> {
        return this.translateLibraryItems(this.quickToolLibraries);
    }

    /**
     * Returns group IDs for the given library ID.
     */
    public getGroupsForLibrary( libraryId: string ): string[] {
        if ( libraryId.includes( CUSTOM_SHAPE_LIB_ID_PREFIX )) {
            return [ 'Shapes' ];
        }
        const lib = this.allShapeLibraries.concat(
            this.allIconsLibraries, this.embedsLibraries,
        ).find( library => library.id === libraryId );
        return lib?.groups;
    }

    /**
     * Returns group IDs for the given library ID.
     */
    public getMainGroupForLibrary( libraryId: string ): string {
        if ( libraryId.includes( CUSTOM_SHAPE_LIB_ID_PREFIX )) {
            return 'Shapes';
        }
        const lib = this.allShapeLibraries.find( library => library.id === libraryId );
        return lib?.libGroup || 'Shapes';
    }

    /**
     * Get library by id
     * @param libraryId
     */
    public getLibrary( libraryId: string ): ILibraryListItem {
        const lib = this.allShapeLibraries.find( lib => lib.id === libraryId );
        return lib ? this.translateLibraryItem(lib) : lib;
    }

    /**
     * Returns the category for the library
     */
    public getCategoryForLibrary( libraryId: string ): string {
        if ( libraryId.includes( CUSTOM_SHAPE_LIB_ID_PREFIX )) {
            return 'Shapes';
        }
        const lib = this.allShapeLibraries.find( library => library.id === libraryId );
        return lib?.category;
    }

    /**
     * Returns all the libraries linked to a group.
     */
    public getLibrariesInGroup( groupName: string ): ILibraryListItem[] {
        const libs = this.allShapeLibraries.concat(
            this.allIconsLibraries, this.embedsLibraries,
        ).filter( libItem => libItem.groups.includes( groupName ));
        return this.translateLibraryItems(libs);
    }

    /**
     *  Takes in main category and return children to build treeview
     * @param category
     */
    public getCategoryChildren( category: string ): { libraries: ILibraryListItem[], groups: string[] } {
        const libraries = [];
        let groups = [];
        this.allShapeLibraries.map( libItem => {
            if ( libItem.category === category ) {
                if ( libItem.groups.length < 1 ) {
                    libraries.push( libItem );
                }
            }
        });
        groups = [ ...Object.keys( this.allLibraryCategories.find( cat => cat.id === category ).groups || {}) ];
        return { libraries: this.translateLibraryItems(libraries), groups: uniq( groups ) };
    }

    /**
     * Returns the category definition
     * @param category
     */
    public getCategoryDefinition( category: string ): ILibraryCategoryDefinition {
        return this.allLibraryCategories.find( i => i.id === category );
    }

    /**
     * Takes in a group and returns the category the group falls in
     * @param group
     */
    public getCategoryDefinitionFromGroup( group: string ): ILibraryCategoryDefinition {
        const category = this.getLibrariesInGroup( group )[ 0 ].category;
        return this.allLibraryCategories.filter( i => i.id === category )[ 0 ];
    }

    /**
     * takes in the libraryId and returns the group and category definition
     * @param libId
     */
    public getLibraryDeepView( libId: string ): { definition: ILibraryCategoryDefinition, group: string } {
        let definition = null;
        let group = '';
        const lib = this.allShapeLibraries.find( library => library.id === libId );

        if ( lib ) {
            group = lib.groups[0];
            definition = this.allLibraryCategories.find( i => i.id === lib.category );
        } else if ( libId.includes( CUSTOM_SHAPE_LIB_ID_PREFIX )) {
            definition = this.allLibraryCategories.find( i => i.id === 'Custom Databases' );
        }

        return { definition, group };
    }

    /**
     * Takes a library and returns other libraries in the same group, or same category if library not in group
     * @param libId
     */
    public getLibrabriesInGroupCategory( libId: string ): Array<ILibraryListItem> {
        const lib = this.allShapeLibraries.find( library => library.id === libId );
        if ( lib.groups.length ) {
            return this.getLibrariesInGroup( lib.groups[0]);
        } else {
            const libs = this.allShapeLibraries.filter(
                library => library.category === lib.category && library.groups.length === 0 );
            return this.translateLibraryItems(libs);
        }
    }
}
